import { Component, OnInit, EventEmitter, Output, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ColorService } from "src/app/services/color.service";
import { CustomEntity } from "../../../models/customEntity.model";
import { CustomEntityService } from "../../../services/customEntity.service";
import { CustomField } from "../../../models/customField.model";
import { CustomFieldModel } from "./../../../models/customFieldModel";
import { CustomObjectService } from "../../../services/customObject.service";
import { Parameter, ParameterLookup } from "./../../../models/parameter.model";
import { ParameterService } from "../../../services/parameter.service";
import { Ppe } from "./../../../models/ppe.model";
import { PpeService } from "./../../../services/ppe.service";
import { SEG } from "src/app/models/seg.model";
import { SegService } from "../../../services/seg.service";
import { SensorAssignment } from "../../../models/sensorAssignment.model";
import { TaskAssignmentExposureContribution } from "../../../models/taskAssignment.model";
import { TaskAssignmentService } from "src/app/services/taskAssignment.service";
import { SensorAssignmentService } from "../../../services/sensorAssignment.service";
import { forkJoin, Subscription } from "rxjs";
import { CommonService } from "../../../services/common.service";
import { normalizeThresholdColorHex } from "src/app/shared/utils/threshold-color.util";

import type * as am5xy from "@amcharts/amcharts5/xy";
import { LabResult } from "../../../models/exposureRequest.model";
import { PpeAssignment } from "src/app/models/ppeAssignment.model";
import { LocalStorageConstants } from "src/app/shared/constants/localStorage.constants";
import { readCachedSelection } from "src/app/shared/utils/cached-selection.util";

import { trackById, trackByIndex } from "src/app/shared/utils/track-by.util";
@Component({
  standalone: false,
    selector: "app-report-body",
    templateUrl: "./report-body.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./report-body.component.scss"],
})
export class ReportBodyComponent implements OnInit {
    constructor(
        private sensorAssignmentService: SensorAssignmentService,
        private route: ActivatedRoute,
        private customEntityService: CustomEntityService,
        private customFieldService: CustomObjectService,
        private segService: SegService,
        private colorService: ColorService,
        private parameterService: ParameterService,
        private ppeService: PpeService,
        private taskAssignmentService: TaskAssignmentService,
        private commonService: CommonService,
    ) {}

    @Output() graphReady = new EventEmitter<boolean>();
    sensorAssignmentData: SensorAssignment;
    sensorAssignmentId: string;
    customEntity: CustomEntity;
    customFields: CustomField[];
    customFieldModel: CustomFieldModel;
    seg: SEG;
    parameters: Parameter[];
    sensorLabResults: LabResult[];
    selectedSensorLabResult: LabResult;
    selectedLabResultsByParamId = new Map<string, LabResult>();
    parameterColorByParamId = new Map<string, string>();
    parameterColorOpaqueByParamId = new Map<string, string>();
    selectedRisk: ParameterLookup;
    sensorLabResult: LabResult;
    ppeAssignmentsTemp: PpeAssignment[] = [];
    ppeAssignments: Ppe[] = [];
    readonly CHART_DIV_ID = "report-body-chart";
    chart: am5xy.XYChart;
    parametersLoaded = false;
    subscriptions: Subscription[] = [];
    selectedParameter: ParameterLookup;
    selectedParameterReport: Parameter;
    taskAssignmentDataSource: TaskAssignmentExposureContribution[];

    ngOnInit(): void {
        this.Initialize();
    }

    private Initialize() {
        this.loadHeaderData();
        this.sensorAssignmentId = this.route.snapshot.params.id;
        this.loadSampleDetails(this.sensorAssignmentId);
    }

    loadHeaderData() {
        this.selectedParameter = readCachedSelection<ParameterLookup>(
            LocalStorageConstants.selectedParameter,
        );
    }

    private loadSampleDetails(id: string | undefined) {
        forkJoin({
            sensorAssignmentData: this.sensorAssignmentService.getById(id),
            sensorLabResults: this.sensorAssignmentService.getLabResults(
                id,
                null,
            ),
        }).subscribe((data) => {
            this.sensorLabResults = data.sensorLabResults;
            this.sensorAssignmentData = data.sensorAssignmentData;
            this.getLabResult(this.selectedParameter?.id);
            this.loadRiskCategories();
            this.getCustomEntity(this.sensorAssignmentData.endPointId);
            this.getSegment(this.sensorAssignmentData.segId);
        });
    }

    public getLabResult(id) {
        if (this.sensorLabResults) {
            this.sensorLabResult = this.sensorLabResults.find(
                (x) => x.parameterId === id,
            );
        }
    }

    public getSelectedSensorLabResult(parameterId: string): boolean {
        if (this.sensorLabResults && this.sensorLabResults.length > 0) {
            const result = this.sensorLabResults.find(
                (result) => result.parameterId === parameterId,
            );
            if (result) {
                this.selectedSensorLabResult = result;
                this.selectedParameterReport = this.parameters.find((res) => {
                    return res.id === parameterId;
                });
                return true;
            }
        }
    }

    private getPpe(id: string | undefined) {
        this.ppeService.getById(id).subscribe((data) => {
            const existingPpe = this.ppeAssignments.find(
                (ppe) => ppe.id === data.id,
            );
            if (!existingPpe) {
                this.ppeAssignments.push(data);
            }
        });
    }

    private getCustomEntity(id: string | undefined) {
        this.customEntityService.getCustomEntity(id).subscribe((data) => {
            this.customEntity = data;
            this.getCustomObjectFields(this.customEntity.customObjectId);
        });
    }

    private getExposureContributions(id: string | undefined) {
        this.taskAssignmentService
            .getExposureContributions(id, this.selectedParameter?.id)
            .subscribe((data) => {
                this.taskAssignmentDataSource = data;
                this.taskAssignmentDataSource.forEach((taskAssignmentData) => {
                    taskAssignmentData.taskAssignments.forEach(
                        (taskAssignment, index) => {
                            const firstPpe = taskAssignment.ppeAssignments?.[0];
                            if (this.ppeAssignmentsTemp.length > 0 && firstPpe) {
                                const exists = this.ppeAssignmentsTemp.find(
                                    (task) => task.ppeId === firstPpe.ppeId,
                                );
                                if (!exists) {
                                    this.ppeAssignmentsTemp.push(firstPpe);
                                }
                            }
                        },
                    );
                });
                this.ppeAssignmentsTemp.forEach((ppeAssignment) => {
                    this.getPpe(ppeAssignment.ppeId);
                });

                this.taskAssignmentDataSource = this.commonService
                    .computeMassContribution(this.taskAssignmentDataSource)
                    .filter((task) => task.massContributionPercentageNum >= 30);
            });
    }

    onGraphReady(isReady: boolean) {
        this.graphReady.emit(isReady);
    }

    private loadRiskCategories(
        filter = "",
        orderBy = "",
        sort = "desc",
        page = 0,
        pageSize = 100,
    ) {
        const labResultData = this.sensorLabResults;
        this.parameterService
            .get(filter, orderBy, sort, page, pageSize, "")
            .subscribe((res) => {
                if (res?.data && res.count) {
                    this.parameters = res?.data;
                    this.parameters = this.parameters.filter(
                        (parameterData) => {
                            return labResultData.some((labResult) => {
                                return (
                                    labResult.parameterId === parameterData.id
                                );
                            });
                        },
                    );
                    const labMap = new Map<string, LabResult>();
                    const colorMap = new Map<string, string>();
                    const colorOpaqueMap = new Map<string, string>();
                    this.parameters.forEach((param) => {
                        const result = this.sensorLabResults.find(
                            (r) => r.parameterId === param.id,
                        );
                        if (result) {
                            labMap.set(param.id, result);
                            colorMap.set(
                                param.id,
                                this.getColorForParameter(param, false),
                            );
                            colorOpaqueMap.set(
                                param.id,
                                this.getColorForParameter(param, true),
                            );
                        }
                    });
                    this.selectedLabResultsByParamId = labMap;
                    this.parameterColorByParamId = colorMap;
                    this.parameterColorOpaqueByParamId = colorOpaqueMap;
                    this.selectedRisk = this.selectedParameter;
                    this.parametersLoaded = true;
                    this.getExposureContributions(this.sensorAssignmentId);
                }
            });
    }

    getColorForParameter(
        parameterData: Parameter,
        setOpacity: boolean,
    ): string {
        let color = "000000";
        // A parameter with no configured bands yet (or a malformed/absent
        // ParameterThresholds response) can't be colored; bail out with the
        // default colour so this dereference can't throw and abort the report render.
        const thresholds = parameterData.thresholds;
        if (!thresholds?.length) {
            return this.colorService.convertHexToRGBA(
                color,
                setOpacity ? 0.1 : 1,
            );
        }
        this.sensorLabResults.forEach((result) => {
            if (result.parameterId === parameterData.id) {
                for (const threshold of thresholds) {
                    color = normalizeThresholdColorHex(threshold.colorHex) ?? color;
                    if (
                        result.value >= threshold.minValue &&
                        (threshold.maxValue == null || result.value <= threshold.maxValue)
                    ) {
                        break;
                    }
                }
            }
        });
        return setOpacity
            ? this.colorService.convertHexToRGBA(color, 0.1)
            : this.colorService.convertHexToRGBA(color, 1);
    }

    private getSegment(id: string | undefined) {
        this.segService.getById(id).subscribe((data) => {
            this.seg = data;
        });
    }

    private getCustomObjectFields(id: string | undefined) {
        this.customFieldService.getCustomObjectFields(id).subscribe((data) => {
            this.customFields = data;
            this.customFieldModel = new CustomFieldModel();
            this.customFieldModel.firstName =
                this.customFields.find((f) => f.name === "First Name")
                    ?.property || "";
            this.customFieldModel.lastName =
                this.customFields.find((f) => f.name === "Last Name")
                    ?.property || "";
            this.customFieldModel.role =
                this.customFields.find((f) => f.name === "Role")?.property ||
                "";
            this.customFieldModel.employeeId =
                this.customFields.find((f) => f.name === "EmployeeID")
                    ?.property || "";
            for (const prop in this.customFieldModel) {
                if (this.customFieldModel.hasOwnProperty(prop)) {
                    const propName = this.customFieldModel[prop];
                    this.customFieldModel[prop] =
                        this.customEntity[propName] || "";
                }
            }
        });
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}
